import Box from '@mui/material/Box';
import { keyframes, useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';

import { easing, glass } from '../theme';
import AppearanceMenu from './AppearanceMenu';
import LanguageMenu from './LanguageMenu';

const drift = keyframes`
  to { transform: translate(60px, 40px) scale(1.12); }
`;

// No blur, as on the home screen: Safari on a phone keeps the last frame of an animated filter.
const rise = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.985); }
`;

/// The screens somebody sees before the application itself - signing in, setting the server
/// up - stand on the website's hero: two slow orbs of the logo's teal, and a grid that fades
/// out from the middle. Whatever is put on it arrives the way the website's sections do. The
/// language and the colours can be changed from the corner before anybody is signed in.
export function Stage({ children }: { children: ReactNode }) {
  const dark = useTheme().palette.mode === 'dark';

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        p: 2,
        overflow: 'clip',
        isolation: 'isolate',
      }}
    >
      <Box aria-hidden sx={{ position: 'absolute', inset: 0, zIndex: -1, pointerEvents: 'none' }}>
        <Box
          sx={{
            position: 'absolute',
            width: 820,
            height: 620,
            left: '50%',
            top: -260,
            translate: '-50% 0',
            borderRadius: '50%',
            filter: 'blur(90px)',
            opacity: dark ? 0.55 : 0.35,
            background: 'radial-gradient(closest-side, rgba(19, 216, 210, 0.55), transparent)',
            animation: `${drift} 18s ease-in-out infinite alternate`,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: 560,
            height: 460,
            left: '12%',
            top: '45%',
            borderRadius: '50%',
            filter: 'blur(90px)',
            opacity: dark ? 0.55 : 0.3,
            background: 'radial-gradient(closest-side, rgba(10, 120, 170, 0.45), transparent)',
            animation: `${drift} 22s ease-in-out infinite alternate-reverse`,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: dark
              ? 'linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px),'
                + ' linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)'
              : 'linear-gradient(rgba(16,19,25,0.05) 1px, transparent 1px),'
                + ' linear-gradient(90deg, rgba(16,19,25,0.05) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            maskImage: 'radial-gradient(ellipse 60% 55% at 50% 30%, #000 0%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse 60% 55% at 50% 30%, #000 0%, transparent 75%)',
          }}
        />
      </Box>

      {/* Outside what arrives: something moving is the frame of anything fixed inside it. */}
      <Box sx={{ position: 'fixed', insetBlockStart: 12, insetInlineEnd: 12, display: 'flex', zIndex: 1 }}>
        <AppearanceMenu />
        <LanguageMenu />
      </Box>

      <Box sx={{ width: '100%', display: 'grid', justifyItems: 'center', animation: `${rise} 1.1s ${easing.out} both` }}>
        {children}
      </Box>
    </Box>
  );
}

/// The frosted panel a form stands in on a stage.
export function StagePanel({ children, maxWidth }: { children: ReactNode; maxWidth: number }) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        ...glass(theme.palette.mode, 'thin'),
        width: '100%',
        maxWidth,
        p: { xs: 3, sm: 4.5 },
        borderRadius: '28px',
        border: '1px solid',
        borderColor: dark ? 'rgba(255,255,255,0.1)' : 'divider',
        background: dark
          ? 'linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.025))'
          : 'rgba(255,255,255,0.72)',
        boxShadow: dark
          ? '0 1px 0 rgba(255,255,255,0.1) inset, 0 30px 80px -30px rgba(0,0,0,0.8)'
          : '0 1px 0 #fff inset, 0 30px 70px -34px rgba(16,19,25,0.35)',
      }}
    >
      {children}
    </Box>
  );
}
